import { useMemo, useCallback, useRef, useState } from "react";
import { useRouter } from "next/router";
import { useSession } from "next-auth/react";
import {
  Box,
  Table,
  Thead,
  Tr,
  Th,
  Flex,
  Popover,
  PopoverTrigger,
  chakra,
  Icon,
  PopoverContent,
  PopoverArrow,
  PopoverBody,
  Tbody,
  Center,
  Tag,
  Text,
  Tooltip,
  Checkbox,
  Image,
  Td,
  Button,
  Link,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  useColorModeValue,
  useDisclosure,
} from "@chakra-ui/react";
import { AddIcon, TriangleDownIcon, TriangleUpIcon } from "@chakra-ui/icons";
import AutoSizer from "react-virtualized-auto-sizer";
import PuffLoader from "react-spinners/PuffLoader";
import * as Bowser from "bowser";
import { FiFilter } from "react-icons/fi";
import { FixedSizeList } from "react-window";
import {
  useTable,
  useSortBy,
  useRowSelect,
  useBlockLayout,
  useFilters,
} from "react-table";
import { layerStyle, radius, textStyle, variant } from "theme/names";
import { MatchString, NumberRangeFilter } from "components/react-table/filters";
import { roundUp } from "utils/helpers";
import { TEXT_COLOR_LIGHT, TEXT_COLOR_DARK } from "./utils";
import { useMiscellaneousContext } from "context/miscellaneousContext";
import { RiSendPlaneLine } from "react-icons/ri";
import { getAccount } from "utils/sessionHelper";
import { ConfirmPopup } from "components/popups/confirmPopup";
import { LemListIcon } from "components/page/icons/LemListIcon";
import { PushLemlistPopup } from "components/popups/pushLemlistPopup";
import { AddToExistingListPopup } from "./add-to-existing-list-popup";
import { useTweetContext } from "context/tweetContext";

interface Props {
  people: any;
  isLoading?: boolean;
}

export function DataTable({ people, isLoading }: Props) {
  const [addToListMode, setAddToListMode] = useState<"new" | "existing">("new");
  const textColor = useColorModeValue(TEXT_COLOR_LIGHT, TEXT_COLOR_DARK);
  const blue50 = useColorModeValue("blue.50", "whiteAlpha.100");
  const { data: session } = useSession() ?? {};
  const nameFilterRef = useRef(null);
  const positionFilterRef = useRef(null);
  const companyRef = useRef(null);
  const locationRef = useRef(null);
  const followersRef = useRef(null);
  const miscellaneousContext: any = useMiscellaneousContext();
  const tweetContext: any = useTweetContext();
  const {
    isOpen: isOpenLemlistPopup,
    onOpen: onOpenLemlistPopup,
    onClose: onCloseLemlistPopup,
  } = useDisclosure();
  const {
    isOpen: isOpenLemlistExplorePopup,
    onOpen: onOpenLemlistExplorePopup,
    onClose: onCloseLemlistExplorePopup,
  } = useDisclosure();
  const {
    isOpen: isAddListOpen,
    onOpen: onAddToListOpen,
    onClose: onAddToListClose,
  } = useDisclosure();
  const router = useRouter();

  const data = useMemo(() => {
    return people.map((p) => {
      return {
        ...p,
        nameAndUsername: {
          name: p.name,
          username: p.username,
        },
      };
    });
  }, [people]);

  const columns = useMemo(
    () => [
      {
        Header: "",
        accessor: "image",
        disableSortBy: true,
        width: 80,
        disableFilters: true,
        Cell: ({ value }) => (
          <Box>
            <Image
              src={value}
              alt="profile image"
              h="10"
              w="10"
              mx="auto"
              borderRadius="50%"
              maxW="unset"
              fallbackSrc="/emptyProfile.png"
            />
          </Box>
        ),
      },
      {
        Header: "name",
        accessor: "nameAndUsername",
        tooltipText: "Name",
        sortDescFirst: true,
        width: 125,
        Filter: (props) => (
          <MatchString
            {...props}
            ref={nameFilterRef}
            placeholder="Search name..."
          />
        ),
        filterRef: nameFilterRef,
        Cell: ({ value }) => (
          <Link
            onClick={(e) => e.stopPropagation()}
            href={"https://www.linkedin.com/in/" + value.username}
            isExternal
          >
            <Text
              textStyle={textStyle["body.medium.light"]}
              fontWeight="500"
              maxW={value ? "32" : "auto"}
              mr="2"
              noOfLines={1}
              wordBreak={"break-all"}
              textAlign="start"
            >
              {value.name}
            </Text>
          </Link>
        ),
      },
      {
        Header: "position",
        accessor: "company_position",
        tooltipText: "Position",
        sortDescFirst: true,
        width: 225,
        Filter: (props) => (
          <MatchString
            {...props}
            ref={positionFilterRef}
            placeholder="Search position..."
          />
        ),
        filterRef: positionFilterRef,
        Cell: ({ value }) => (
          <Text
            textStyle={textStyle["body.medium.light"]}
            fontWeight="500"
            maxW={value ? "44" : "auto"}
            mr="2"
            noOfLines={1}
            wordBreak={"break-all"}
            textAlign="start"
          >
            {value ? value : "N/A"}
          </Text>
        ),
      },
      {
        Header: "Company",
        accessor: "company_name",
        tooltipText: "Current Company",
        disableSortBy: true,
        width: 150,
        Filter: (props) => (
          <MatchString
            {...props}
            ref={companyRef}
            placeholder="Search company..."
          />
        ),
        filterRef: companyRef,
        Cell: ({ value, row }) => {
          return (
            <Text
              textStyle={textStyle["body.medium.light"]}
              maxW={value ? "200px" : "auto"}
              noOfLines={1}
              wordBreak={"break-all"}
              textAlign="start"
            >
              {value ? value : "N/A"}
            </Text>
          );
        },
      },
      {
        Header: "location",
        accessor: "location.default",
        tooltipText: "Location",
        sortDescFirst: true,
        width: 225,
        Filter: (props) => (
          <MatchString
            {...props}
            ref={locationRef}
            placeholder="Search location..."
          />
        ),
        filterRef: locationRef,
        Cell: ({ value }) => (
          <Text
            textStyle={textStyle["body.medium.light"]}
            fontWeight="500"
            maxW={value ? "44" : "auto"}
            mr="2"
            noOfLines={1}
            wordBreak={"break-all"}
            textAlign="start"
          >
            {value ? value : "N/A"}
          </Text>
        ),
      },
      {
        Header: ({ value }) => (
          <Text pl={0} textAlign="left">
            FOLLOWERS
          </Text>
        ),
        accessor: "followersCount",
        tooltipText: "No. of Followers",
        sortDescFirst: true,
        isNumeric: true,
        Filter: (props) => <NumberRangeFilter {...props} ref={followersRef} />,
        width: 140,
        filterRef: followersRef,
        filter: "between",
        Cell: ({ value }) => <Text textAlign="center">{roundUp(value)}</Text>,
      },
      {
        Header: "Company Tags",
        accessor: "companyIndustryTags",
        tooltipText: "Number of DM already sent",
        width: 500,
        disableSortBy: true,
        disableFilters: true,
        Cell: ({ value, row }) => {
          return (
            <Flex w="500px" wrap="wrap">
              {value?.slice(0, 10)?.map((tag: any, index) => (
                <Tag
                  maxW="80px"
                  noOfLines={1}
                  wordBreak={"break-all"}
                  size="xs"
                  variant="outline"
                  fontSize="xs"
                  key={tag + index}
                  p={1}
                  m={"2px"}
                  colorScheme="blue"
                >
                  {tag}
                </Tag>
              ))}
            </Flex>
          );
        },
      },
    ],
    []
  );

  const defaultColumn = useMemo(
    () => ({
      width: 90,
    }),
    []
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    selectedFlatRows,
    totalColumnsWidth,
    setAllFilters,
    toggleAllRowsSelected,
    state,
    initialState,
  } = useTable(
    { columns, data, defaultColumn },
    useFilters,
    useSortBy,
    useBlockLayout,
    useRowSelect,
    (hooks) => {
      hooks.visibleColumns.push((columns) => [
        {
          Header: ({ getToggleAllRowsSelectedProps }) => {
            const props = {
              ...getToggleAllRowsSelectedProps({ title: undefined }),
            };
            delete props.indeterminate;
            return (
              <Tooltip label="Select All">
                <span>
                  <Checkbox isChecked={props.checked} {...props} />
                </span>
              </Tooltip>
            );
          },
          Cell: ({ row }) => {
            const props = {
              ...row.getToggleRowSelectedProps({ title: undefined }),
            };
            delete props.indeterminate;
            return (
              <Tooltip label="Select">
                <span onClick={(e) => e.stopPropagation()}>
                  <Checkbox
                    isChecked={props.checked}
                    title="Select"
                    {...props}
                  />
                </span>
              </Tooltip>
            );
          },
          width: 25,
        },
        ...columns,
      ]);
    }
  );

  const RenderRow = useCallback(
    ({ index, style }) => {
      const row = rows[index];
      prepareRow(row);
      return (
        <Tr
          as={Box}
          {...row.getRowProps({ style })}
          cursor="pointer"
          px="3"
          alignItems="center"
          _hover={{
            background: blue50,
          }}
          onClick={() => {
            tweetContext.setIsOpen(false);
            miscellaneousContext.setSidebarPerson(row.original);
            miscellaneousContext.setIsPersonSidebarOpen(true);
          }}
        >
          {row.cells.map((cell) => (
            <Td
              {...cell.getCellProps()}
              as={Box}
              textAlign="center"
              paddingX="1"
              borderBottom="none"
            >
              {cell.render("Cell")}
            </Td>
          ))}
        </Tr>
      );
    },
    [prepareRow, rows, selectedFlatRows]
  );

  const selectedPeople = selectedFlatRows?.map((s) => {
    delete s.original?.actions;
    return s.original;
  });

  let safariStyles = {};
  if (typeof window !== "undefined") {
    const browser = Bowser.getParser(window.navigator.userAgent);
    const isSafari = browser?.getBrowserName()?.includes("Safari");

    if (isSafari) {
      safariStyles = {
        display: "block",
        overflow: "auto",
      };
    }
  }

  return (
    <Box>
      {people?.length > 0 && (
        <Flex
          justifyContent="end"
          flexWrap="wrap"
          gap="12px"
          alignItems="center"
          mt="3"
        >
          {selectedFlatRows?.length > 0 && (
            <Flex
              justifyContent="end"
              flexWrap="wrap"
              gap="12px"
              alignItems="center"
            >
              <Text
                textStyle={textStyle["body.medium.light"]}
                color={textColor}
                as="i"
                fontSize="xs"
                fontWeight="bold"
              >
                {selectedFlatRows.length} selected
              </Text>
            </Flex>
          )}
          <Button
            boxShadow="sm"
            variant={variant.Button.secondary}
            leftIcon={<LemListIcon height={14} width={14} />}
            onClick={() => {
              if (getAccount(session)?.keyLemlist) {
                onOpenLemlistPopup();
              } else {
                onOpenLemlistExplorePopup();
              }
            }}
          >
            Push to lemlist
          </Button>
          {/* {selectedFlatRows?.length > 0 && (
            <Flex
              justifyContent="end"
              flexWrap="wrap"
              gap="12px"
              alignItems="center"
            >
              <Button
                boxShadow="sm"
                variant={variant.Button.secondary}
                leftIcon={<RiSendPlaneLine height={3} width={3} />}
                onClick={() => {
                  miscellaneousContext.setCrmDm(selectedPeople);
                  router.push(
                    { pathname: "/dm", query: { from: "crm" } },
                    "/dm"
                  );
                }}
              >
                Send DM
              </Button>
            </Flex>
          )} */}
          <Menu>
            <MenuButton as={Button} variant="primary" leftIcon={<AddIcon />}>
              Add to list
            </MenuButton>
            <MenuList>
              <MenuItem
                onClick={() => {
                  setAddToListMode("new");
                  onAddToListOpen();
                }}
              >
                Add to new list
              </MenuItem>
              <MenuItem
                onClick={() => {
                  setAddToListMode("existing");
                  onAddToListOpen();
                }}
              >
                Add to existing list
              </MenuItem>
            </MenuList>
          </Menu>
        </Flex>
      )}
      <Box
        mt="6"
        overflow="auto"
        minH="96"
        layerStyle={layerStyle.bgBorderGray}
        borderRadius={radius.lg}
      >
        <Table
          as={Box}
          {...getTableProps()}
          css={{ borderCollapse: "separate" }}
          variant={"simpleNoBg"}
        >
          <Thead as={Box}>
            {headerGroups.map((headerGroup) => (
              <Tr {...headerGroup.getHeaderGroupProps()} px="3" as={Box}>
                {headerGroup.headers.map((column, index) => (
                  <Th {...column.getHeaderProps()} paddingX="1" as={Box}>
                    <Flex alignItems="center" h="100%">
                      <Box
                        {...column.getSortByToggleProps({ title: undefined })}
                      >
                        {column.render("Header")}
                      </Box>
                      {column.canFilter ? (
                        <Popover initialFocusRef={column.filterRef}>
                          <PopoverTrigger>
                            <Box cursor="pointer">
                              <chakra.span>
                                <Icon as={FiFilter} ml="2" />
                              </chakra.span>
                            </Box>
                          </PopoverTrigger>
                          <PopoverContent>
                            <PopoverArrow />
                            <PopoverBody p="4">
                              {column.render("Filter")}
                            </PopoverBody>
                          </PopoverContent>
                        </Popover>
                      ) : null}
                      {column.isSorted ? (
                        <chakra.span
                          pl="3"
                          {...column.getSortByToggleProps({ title: undefined })}
                        >
                          {column.isSortedDesc ? (
                            <TriangleDownIcon aria-label="sorted descending" />
                          ) : (
                            <TriangleUpIcon aria-label="sorted ascending" />
                          )}
                        </chakra.span>
                      ) : null}
                    </Flex>
                  </Th>
                ))}
              </Tr>
            ))}
          </Thead>
          <Tbody
            as={Box}
            {...getTableBodyProps()}
            height={!isLoading && rows.length > 0 ? "70vh" : "unset"}
            {...safariStyles}
          >
            <AutoSizer>
              {({ height }) =>
                rows.length > 0 && (
                  <FixedSizeList
                    height={height}
                    itemCount={rows.length}
                    itemSize={70}
                    width={totalColumnsWidth}
                  >
                    {RenderRow}
                  </FixedSizeList>
                )
              }
            </AutoSizer>
          </Tbody>
        </Table>
        {isLoading && (
          <Center h="100px" w="100%">
            <PuffLoader
              color={"#0A66C2"}
              loading={isLoading}
              cssOverride={{
                display: "block",
                margin: "0 auto",
                borderColor: "red",
              }}
              size={50}
            />
          </Center>
        )}
        {!isLoading && people?.length === 0 && (
          <Center my={20} flexDir="column">
            <Text>No results found</Text>
            <Text mt={10}>Check your filters manually and update them</Text>
          </Center>
        )}
      </Box>
      <ConfirmPopup
        isOpen={isOpenLemlistExplorePopup}
        onClose={onCloseLemlistExplorePopup}
        callback={() => {
          router.push("/settings?tab=integrations");
        }}
        cta="Add your lemlist key"
        title="Taplio x lemlist integration"
        body={
          <Box>
            <Text textStyle={textStyle["body.medium.light"]}>
              Turn your LinkedIn audience into nurtured leads and future clients
              with our lemlist integration.
            </Text>
            <Text textStyle={textStyle["body.medium.light"]} mt={4}>
              If you already have a lemlist account, copy your lemlist API key
              in the{" "}
              <Link
                href="/settings?tab=integrations"
                textDecoration="underline"
              >
                integration section
              </Link>{" "}
              and start adding your Taplio contacts to lemlist campaigns.
            </Text>
          </Box>
        }
      />
      <PushLemlistPopup
        leads={selectedPeople?.length > 0 ? selectedPeople : people}
        isOpen={isOpenLemlistPopup}
        onClose={onCloseLemlistPopup}
        disableAutoPush={true}
        refetchAll={true}
      />
      <AddToExistingListPopup
        mode={addToListMode}
        isOpen={isAddListOpen}
        onClose={onAddToListClose}
        people={selectedPeople?.length > 0 ? selectedPeople : people}
        refetchAll={true}
      />
    </Box>
  );
}
